import {FunctionComponent, useState} from "react";
import {Button, ButtonProps, Container, Grid, Paper, Slide, Typography, Zoom} from "@mui/material";
import {documentToReactComponents} from "@contentful/rich-text-react-renderer";
import {ImageAsset, Link} from "../../types";
import styled from "styled-components";
import {Document} from "@contentful/rich-text-types";
import {media} from "../../../styles/styles";

const StyledButton = styled(Button)<ButtonProps>`
  && {
    position: absolute;
    ${({ color }) =>
      color === "primary"
        ? `
    background-color: #de4a22;
    color: #fff;
    &:hover {
      background-color: #de4a22;
      color: #fff;
    }`
        : `background-color: #fff;
    color: #de4a22;`}
    padding: 0.8rem 1rem;
    font-size: 1.5em;
    min-width: 360px;
    min-height: 62px;
    ${media.phone`
    min-width: auto;
    padding: 0.8rem 0;
     width:100%;
     border-radius: 12px;
    overflow: hidden;
  `};
    border-radius: 12px;
    overflow: hidden;
  }
`;
const MainFeaturedPost = styled(Paper)`
  min-height: calc(100vw * 0.375);
  display: flex;
  
  && {
    position: relative;
    background-color: transparent;
    overflow: hidden;
    width: 100%;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    
    ${media.tablet`
    font-size: .9rem;
    height: fit-content;
  `}
    ${media.phone`
    font-size: .8rem;
  `}
  }
  
  &>div {
    display: flex;
  }
`;
const MainFeaturedPostContent = styled(Grid)`
  && {
    position: relative;
    margin: auto 0 auto 0;
    color: #fff;
    border-radius: 0;
    padding: 1rem 0 5rem;
    
    ${media.tablet`
    padding: 4rem 2rem 6rem;
    margin: auto 0;
  `}
  }
  
  h1 {
    font-size: 3rem;
    ${media.tablet`
    font-size: 2rem;
  `}
  }
`;
const Overlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background: transparent
    linear-gradient(90deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 33%, transparent 60%) 0% 0%
    no-repeat padding-box;
  mix-blend-mode: multiply;
  
  ${media.tablet`
  background: transparent
    linear-gradient(90deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 56%, transparent 100%) 0% 0%
    no-repeat padding-box;
  `}
`;
const Subtitle = styled.div`
  font-size: 2rem;
  ${media.tablet`
    font-size: 1.2rem;
    line-height: 1.3;
  `}
`;
const HeroImage = styled.img`
  margin-bottom: -6px;
  height: auto;
  margin-left: 60px;
  width: auto;
  ${media.tablet`
    width: 100%;
    margin-left:0;
  `}
`;
const HeroImageContent = styled(Grid)`
  && {
    z-index: 2;
    ${media.tablet`
      max-width: 75%;
      `}
  }
`;
const ButtonWrapper = styled.div`
  position: relative;
  height: 62px;
  width: 100%;
  overflow: hidden;
  border-radius: 12px;
  ${media.tablet`
    margin-top: 3rem;
  `}
`;
interface Props {
  hero: {
    featureImage: ImageAsset;
    title: string;
    content: Document;
    callToAction: Link;
  };
}

const HeroHome: FunctionComponent<Props> = ({
  hero: { featureImage, title, content, callToAction },
}) => {
  const hero = featureImage ? {
    image: featureImage.fields.file.url,
    imageText: featureImage.fields.title,
  } : {};

  const description = content ? documentToReactComponents(content) : "";
  const linkText = callToAction ? callToAction.fields.linkTitle : "";
  const linkHoverText = callToAction ? callToAction.fields.linkHoverText : "";
  const linkUrl = callToAction ? callToAction.fields.linkUrl : "";
  const [buttonHovered, setButtonHovered] = useState(false);
  return (
    <MainFeaturedPost>
      <Overlay />
      <Container maxWidth="md" sx={{padding:0}}>
        <Grid container alignItems="center" >
          <MainFeaturedPostContent item xs={12}>
            <Typography
              component="h1"
              variant="h1"
              color="inherit"
              gutterBottom
            >
              {title}
            </Typography>
            <Subtitle>{description}</Subtitle>
            <ButtonWrapper
              onMouseEnter={() => setButtonHovered(true)}
              onMouseLeave={() => setButtonHovered(false)}
            >
              <StyledButton variant="contained" href={linkUrl} >
                <Zoom
                  in={buttonHovered}
                  mountOnEnter
                  unmountOnExit
                  timeout={{ enter: 400, exit: 200, appear: 500 }}
                >
                  <span>{linkHoverText}</span>
                </Zoom>
              </StyledButton>
              <Slide
                direction={"right"}
                in={!buttonHovered}
                mountOnEnter
                unmountOnExit
                timeout={{ enter: 500, exit: 250, appear: 1500 }}
              >
                <StyledButton
                  variant="contained"
                  color="primary"
                  href={linkUrl}
                >
                  {linkText}
                </StyledButton>
              </Slide>
            </ButtonWrapper>
          </MainFeaturedPostContent>
          {hero.image && (
            <HeroImageContent item sm={6} xs={12}>
              <picture>
                <source
                  type="image/webp"
                  srcSet={`${hero.image}?fm=webp&w=400&q=80`}
                  media="(max-width: 400px)"
                />
                <source
                  type="image/webp"
                  srcSet={`${hero.image}?fm=webp&w=557&q=80`}
                  media="(min-width: 401px)"
                />
                <source
                  type="image/jpeg"
                  srcSet={`${hero.image}?w=400&q=80`}
                  media="(max-width: 400px)"
                />
                <source
                  type="image/jpeg"
                  srcSet={`${hero.image}?w=557&q=80`}
                  media="(min-width: 401px)"
                />
                <HeroImage
                  src={`${hero.image}?w=557&q=80`}
                  alt={hero.imageText}
                  width="512"
                  height="557"
                />
              </picture>
            </HeroImageContent>
          )}
        </Grid>
      </Container>
    </MainFeaturedPost>
  );
};
export default HeroHome;
